import { type FC } from 'react'
import { FaCheckCircle } from 'react-icons/fa'

import { Alert, Button, Tag } from '@/components/elements'
import {
  ACTIONS,
  SHIPMENT_ORDER_CSV_IMPORT
} from '@/constants/common/message.constants'

import { shapeRegistrationRow, type PreviewDataRow } from '../../models'

/** 取り出すファイルの種類（失敗した行・修正を反映した CSV） */
export type CutOutKind = 'failed' | 'imported'

/** 取り込み実行の結果サマリ（確認画面の行数からFE側で数えた、ジョブへ送った件数）。 */
export type ImportResultSummary = {
  registeredCount: number
  excludedCount: number
  /** フォーマット保存（任意操作）を行った場合のフォーマット名。 */
  savedFormatName: string | null
  /** 取り出せる件数（失敗した行・修正を反映した CSV）。0 の種類は出さない。切り出せないとき（元の CSV が読めていない）は null */
  cutOutCounts: Record<CutOutKind, number> | null
}

const CUT_OUT_LABEL: Record<CutOutKind, (count: number) => string> = {
  failed: SHIPMENT_ORDER_CSV_IMPORT.DONE_CUT_OUT_FAILED,
  imported: SHIPMENT_ORDER_CSV_IMPORT.DONE_CUT_OUT_IMPORTED
}

/** 登録内容プレビューに表示する代表行数（全件ではなくサンプル表示、frontend-performance.md 準拠の抑制）。 */
const REGISTRATION_PREVIEW_SAMPLE_SIZE = 3

type Props = {
  result: ImportResultSummary
  /** 確認画面のエラー無し行（登録対象）。登録内容プレビューのサンプル抽出元。 */
  registeredRows: readonly PreviewDataRow[]
  /** 失敗した行・修正を反映した CSV をダウンロードする */
  onDownloadCutOut: (kind: CutOutKind) => void
  onClose: () => void
}

/**
 * 最終ステップ: 取り込み実行 結果。
 * 登録件数 / 除外（エラー）件数と、フォーマット保存（任意操作）の結果に加えて、
 * 「登録内容プレビュー」（型変換・グルーピング済みの実データ、先頭N件）を表示する。
 * 取り込み自体はジョブとして非同期で進むため、ここで出す件数は「送った件数」であって登録結果ではない
 * （登録結果・エラーはインポート履歴で確認する）。
 */
export const DoneView: FC<Props> = ({
  result,
  registeredRows,
  onDownloadCutOut,
  onClose
}) => {
  const sample = registeredRows.slice(0, REGISTRATION_PREVIEW_SAMPLE_SIZE)
  const cutOutKinds = (['failed', 'imported'] as const).filter(
    (kind) => (result.cutOutCounts?.[kind] ?? 0) > 0
  )

  return (
    <div className="flex flex-col gap-3">
      <Alert title={SHIPMENT_ORDER_CSV_IMPORT.DONE_MESSAGE}>
        <div
          className="flex flex-col gap-1 text-xs"
          data-testid="shipment-order-csv-import__done-summary"
        >
          <span className="flex items-center gap-1.5 font-medium text-secondary">
            <FaCheckCircle className="text-green" />
            {SHIPMENT_ORDER_CSV_IMPORT.DONE_SUMMARY_REGISTERED(
              result.registeredCount
            )}
            {result.excludedCount > 0 && (
              <span className="text-red">
                {SHIPMENT_ORDER_CSV_IMPORT.DONE_SUMMARY_EXCLUDED(
                  result.excludedCount
                )}
              </span>
            )}
          </span>
          {result.savedFormatName && (
            <span className="text-gray-2">
              {SHIPMENT_ORDER_CSV_IMPORT.DONE_FORMAT_SAVED(
                result.savedFormatName
              )}
            </span>
          )}
        </div>
      </Alert>

      {result.cutOutCounts && cutOutKinds.length > 0 && (
        <div
          className="flex flex-col gap-2 rounded border border-table-border p-3"
          data-testid="shipment-order-csv-import__cut-out"
        >
          <span className="text-xs font-medium text-secondary">
            {SHIPMENT_ORDER_CSV_IMPORT.DONE_CUT_OUT_TITLE}
          </span>
          <span className="text-2xs text-gray-2">
            {SHIPMENT_ORDER_CSV_IMPORT.DONE_CUT_OUT_DESCRIPTION}
          </span>
          <div className="flex flex-wrap gap-2">
            {cutOutKinds.map((kind) => (
              <Button
                key={kind}
                size="small"
                onClick={() => onDownloadCutOut(kind)}
                data-testid={`shipment-order-csv-import__cut-out-${kind}`}
              >
                {CUT_OUT_LABEL[kind](result.cutOutCounts?.[kind] ?? 0)}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div
        className="flex flex-col gap-2 rounded border border-table-border p-3"
        data-testid="shipment-order-csv-import__registration-preview"
      >
        <span className="text-xs font-medium text-secondary">
          {SHIPMENT_ORDER_CSV_IMPORT.REGISTRATION_PREVIEW_TITLE}
        </span>
        <span className="text-2xs text-gray-2">
          {SHIPMENT_ORDER_CSV_IMPORT.REGISTRATION_PREVIEW_DESCRIPTION}
        </span>
        {sample.length === 0 ? (
          <span className="text-2xs text-gray-2">
            {SHIPMENT_ORDER_CSV_IMPORT.REGISTRATION_PREVIEW_EMPTY}
          </span>
        ) : (
          <>
            <span className="text-2xs text-gray-2">
              {SHIPMENT_ORDER_CSV_IMPORT.REGISTRATION_PREVIEW_SAMPLE_NOTE(
                sample.length,
                registeredRows.length
              )}
            </span>
            <div className="flex flex-col gap-3">
              {sample.map((row) => (
                <div
                  key={row.rowNo}
                  className="flex flex-col gap-1.5 rounded bg-neutral-3 p-2.5"
                  data-testid={`shipment-order-csv-import__registration-preview-row-${row.rowNo}`}
                >
                  <span className="text-2xs font-medium text-gray-2">
                    {SHIPMENT_ORDER_CSV_IMPORT.REGISTRATION_PREVIEW_ROW_LABEL(
                      row.rowNo
                    )}
                  </span>
                  {shapeRegistrationRow(row).map((group) => (
                    <div key={group.group} className="flex flex-col gap-0.5">
                      <span className="text-3xs font-medium text-gray-2">
                        {group.groupLabel}
                      </span>
                      <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                        {group.fields.map((field) => (
                          <span
                            key={field.value}
                            className="flex items-baseline gap-1 text-2xs"
                            data-testid={`shipment-order-csv-import__registration-preview-field-${row.rowNo}-${field.value}`}
                          >
                            <span className="text-gray-2">{field.label}</span>
                            <span className="font-medium text-secondary">
                              {field.shaped}
                            </span>
                            {field.kind === 'masterCode' && (
                              <Tag size="small" color="default">
                                {
                                  SHIPMENT_ORDER_CSV_IMPORT.REGISTRATION_PREVIEW_MASTER_CODE_TAG
                                }
                              </Tag>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          onClick={onClose}
          data-testid="shipment-order-csv-import__close"
        >
          {ACTIONS.CLOSE}
        </Button>
      </div>
    </div>
  )
}
